import { Injectable } from '@angular/core';
import {
  PragmaPowerBehaviorDefinition,
  PragmaPowerBehaviorDefinitionsRequest,
} from '@app/interface/pragma-power-behavior.interface';
import {
  PragmaPowerCategoriesRequest,
  PragmaPowerCategory,
} from '@app/interface/pragma-power-categories.interface';
import {
  PragmaPowerDefinition,
  PragmaPowerDefinitionRequest,
} from '@app/interface/pragma-power-definition.interface';
import { ServiceType } from '@app/interface/service';
import { Observable, catchError, from, map, of, throwError } from 'rxjs';
import { GatewayService } from './gateway.service';

import { HttpStatusCode } from '@angular/common/http';
import {
  ERROR_SELF_ASSESSMENT_BEHAVIORS,
  GET_ASSESSMENT_COMMENTS_PATH,
  GET_PRAGMAPOWER_CATEGORIES_PATH,
  GET_PRAGMAPOWER_DEFINITIONS_PATH,
  GET_PRAGMAPOWER_TYPES_PATH,
  getAssessmentBehaviorById,
  getAssessmentPragmaPowersByAssessmentId,
  getPragmaPowerBehaviorDefinitionsByCompetenceId,
  getPragmaticProfileByEmail,
  getPragmaticValorationDataByEmail,
  getPragmaticValorationDataById,
} from '@app/constants';
import {
  AssessmentPragmaPower,
  AssessmentPragmaPowerRequest,
} from '@app/interface/assesment-pragmapower';
import { StartAssessment, StartAssessmentRequest } from '@app/interface/assessment';
import { AssessmentBehavior, AssessmentBehaviorRequest } from '@app/interface/assessment-behavior';
import {
  PragmaPowerType,
  PragmaPowerTypeRequest,
} from '@app/interface/pragma-power-types.interface';
import { PragmaticChapterDetail } from '@app/interface/pragmatic-chapter-detail';
import { PragmaticProfile } from '@app/interface/pragmatic.profile';

@Injectable({
  providedIn: 'root',
})
export class ValorationService {
  constructor(public gateway: GatewayService) {}

  pragmaPowerCategoriesMock!: PragmaPowerCategory[];

  getPragmaPowerCategories(
    pragmaPowerCategoriesParams: PragmaPowerCategoriesRequest
  ): Observable<PragmaPowerCategory[]> {
    const request = { ...pragmaPowerCategoriesParams };
    return from(
      this.gateway
        .doGet<{ data: PragmaPowerCategory[] }>(
          GET_PRAGMAPOWER_CATEGORIES_PATH,
          request,
          ServiceType.AssessmentService
        )
        .then((response) => {
          if (response.success || response.status === HttpStatusCode.Ok) {
            return response.data?.data ?? [];
          }
          return [];
        })
    );
  }

  getPragmaPowerDefinition(
    pragmaPowerCategoriesParams: PragmaPowerDefinitionRequest
  ): Observable<PragmaPowerDefinition[]> {
    const request = { ...pragmaPowerCategoriesParams };
    return from(
      this.gateway
        .doGet<{ data: PragmaPowerDefinition[] }>(
          GET_PRAGMAPOWER_DEFINITIONS_PATH,
          request,
          ServiceType.AssessmentService
        )
        .then((response) => {
          if (response.success || response.status === HttpStatusCode.Ok) {
            return response.data?.data ?? [];
          }
          return [];
        })
    );
  }

  getPragmaPowerBehaviorDefinition(
    pragmaPowerBehaviorId: number,
    pragmaPowerCategoriesParams: PragmaPowerBehaviorDefinitionsRequest
  ): Observable<PragmaPowerBehaviorDefinition[]> {
    const path = getPragmaPowerBehaviorDefinitionsByCompetenceId(pragmaPowerBehaviorId);
    const request = { ...pragmaPowerCategoriesParams };
    return from(
      this.gateway
        .doGet<{ data: PragmaPowerBehaviorDefinition[] }>(
          path,
          request,
          ServiceType.AssessmentService
        )
        .then((response) => {
          if (response.success || response.status === HttpStatusCode.Ok) {
            return response.data?.data ?? [];
          }
          return [];
        })
    );
  }

  getPragmaticProfile(email: string): Observable<PragmaticProfile> {
    return from(
      this.gateway
        .doGet<{ data: PragmaticProfile }>(
          getPragmaticProfileByEmail(email),
          {},
          ServiceType.Assessments
        )
        .then((response) => {
          if (response.success || response.status === HttpStatusCode.Ok) {
            return response.data?.data ?? ({} as PragmaticProfile);
          }
          return {} as PragmaticProfile;
        })
    );
  }

  getPragmaticChapterDetails(email: string): Observable<PragmaticChapterDetail[]> {
    const path = getPragmaticValorationDataByEmail(email);
    return from(
      this.gateway
        .doGet<{ data: PragmaticChapterDetail[] }>(path, {}, ServiceType.AdministrationService)
        .then((response) => {
          if (response.success || response.status === HttpStatusCode.Ok) {
            return response.data?.data ?? [];
          }
          return [];
        })
    );
  }

  getPragmaticChapterDetailsById(pragmaticId: number): Observable<PragmaticChapterDetail[]> {
    const path = getPragmaticValorationDataById(pragmaticId);

    return from(
      this.gateway
        .doGet<{ data: PragmaticChapterDetail[] }>(path, {}, ServiceType.AdministrationService)
        .then((response) => {
          if (response.success || response.status === HttpStatusCode.Ok) {
            return response.data?.data ?? [];
          }
          return [];
        })
    );
  }

  getAssessmentPragmaPowers(
    idAssessment: number,
    assesmentPragmaPowerParams: AssessmentPragmaPowerRequest
  ): Observable<AssessmentPragmaPower[]> {
    if (idAssessment === undefined) return of([]);

    const path = getAssessmentPragmaPowersByAssessmentId(idAssessment);
    const request = { ...assesmentPragmaPowerParams };
    return from(
      this.gateway
        .doGet<{ data: AssessmentPragmaPower[] }>(path, request, ServiceType.AssessmentService)
        .then((response) => {
          if (response.success || response.status === HttpStatusCode.Ok) {
            return response.data?.data ?? [];
          }
          return [];
        })
    );
  }

  getAssessmentBehaviors(
    idAssessment: number,
    assesmentPragmaPowerParams: AssessmentBehaviorRequest
  ): Observable<AssessmentBehavior[]> {
    const path = getAssessmentBehaviorById(idAssessment);
    const request = { ...assesmentPragmaPowerParams };
    return from(
      this.gateway
        .doGet<{ data: AssessmentBehavior[] }>(path, request, ServiceType.AssessmentService)
        .then((response) => {
          if (response.success || response.status === HttpStatusCode.Ok) {
            return response.data?.data ?? [];
          }
          return [];
        })
    );
  }

  getSelfAssessmentBehaviors(
    idAssessment: number,
    assesmentPragmaPowerParams: AssessmentBehaviorRequest
  ): Observable<AssessmentBehavior[]> {
    const path = getAssessmentBehaviorById(idAssessment);
    const request = { ...assesmentPragmaPowerParams };
    return from(
      this.gateway.doGet<{ data: AssessmentBehavior[] }>(
        path,
        request,
        ServiceType.AssessmentService
      )
    ).pipe(
      catchError((error) => {
        return throwError(() => error);
      }),
      map((response) => {
        if (response.success || response.status === HttpStatusCode.Ok) {
          return response.data?.data ?? [];
        }
        throw new Error(ERROR_SELF_ASSESSMENT_BEHAVIORS);
      })
    );
  }

  startSelfAssessment(startAssementRequest: StartAssessmentRequest): Observable<StartAssessment> {
    return from(
      this.gateway
        .doPost<StartAssessment>(
          GET_ASSESSMENT_COMMENTS_PATH,
          startAssementRequest,
          ServiceType.AssessmentService
        )
        .then((response) => {
          if (response.success || response.status === HttpStatusCode.Ok) {
            return response.data ?? ({} as StartAssessment);
          }
          return {} as StartAssessment;
        })
    );
  }

  getPragmaPowerTypes(
    pragmaPowerTypeRequest: PragmaPowerTypeRequest
  ): Observable<PragmaPowerType[]> {
    const request = { ...pragmaPowerTypeRequest };
    return from(
      this.gateway
        .doGet<{ data: PragmaPowerType[] }>(
          GET_PRAGMAPOWER_TYPES_PATH,
          request,
          ServiceType.AssessmentService
        )
        .then((response) => {
          if (response.success || response.status === HttpStatusCode.Ok) {
            return response.data?.data ?? [];
          }
          return [];
        })
    );
  }
}
